/**
 * NotificationHub Component
 * Wraps push notifications in an ErrorBoundary to prevent notification errors
 * from crashing the application. Notifications are silent on error.
 */

import React from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import ErrorBoundary from './ErrorBoundary';

/**
 * PushNotificationRenderer — internal component that uses the hook
 * Isolated inside ErrorBoundary so errors don't propagate to parent
 */
const PushNotificationRenderer: React.FC = () => {
  // Hook initialization and SSE connection setup happens here
  // Errors in the hook or during notification creation are caught by the boundary
  usePushNotifications();

  // This component doesn't render anything; it just initializes the hook
  // The actual notifications are shown via browser Notification API
  return null;
};

/**
 * NotificationHub — error boundary wrapper for notifications
 * If notification initialization fails, the error is silently swallowed
 * and the rest of the app continues to function normally
 */
const NotificationHub: React.FC = () => {
  return (
    <ErrorBoundary>
      <PushNotificationRenderer />
    </ErrorBoundary>
  );
};

export default NotificationHub;
